/**
 * API エンドポイントの URL を構築するサービス
 * - API のパステンプレートと実行時設定から、完全な URL を生成する
 * - API 呼び出し前に URL を組み立てる際に利用する
 */


import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AppConfigService } from './app-config.service';

/** パス・クエリに埋め込める基本プリミティブ型 */
type Primitive = string | number | boolean;

/** URL パス置換用のパラメータ辞書 */
type PathParams = Record<string, Primitive | null | undefined>;

/** URL クエリ文字列生成用のパラメータ辞書 */
type QueryParams = Record<string, Primitive | null | undefined>;

/** Group 階層コンテキスト */
type GroupParentContext = string | null | undefined;

@Injectable({ providedIn: 'root' })
export class ApiEndpointService {
  /** 実行時設定から reporterId などの動的値を参照する */
  constructor(private readonly config: AppConfigService) {}

  /** 一覧検索 API の相対パスを返す */
  get listSearchPath(): string {
    return this.buildApiPath(environment.api.endpoints.listSearch);
  }

  /** 出荷情報 patch API の相対パスを返す */
  get shipPatchPath(): string {
    return this.buildApiPath(environment.api.endpoints.shipPatch);
  }

  /** 入荷情報 patch API の相対パスを返す */
  get incomingPatchPath(): string {
    return this.buildApiPath(environment.api.endpoints.incomingPatch);
  }

  /** ロット情報 patch API の相対パスを返す */
  get lotPatchPath(): string {
    return this.buildApiPath(environment.api.endpoints.lotPatch);
  }

  /** 振分先マスタ検索 API の相対パスを返す */
  get distributionPlaceSearchUrl(): string {
    return this.buildUrl(
      environment.api.masterBaseUrl,
      environment.api.endpoints.distributionPlaceSearch,
      this.defaultPathParams,
    );
  }

  /** API 用の相対パスをテンプレートから構築する */
  buildApiPath(template: string, pathParams?: PathParams, queryParams?: QueryParams): string {
    return this.buildRelativeUrl(template, {
      ...this.defaultPathParams,
      ...pathParams,
    }, queryParams);
  }

  /** Group 作成・取得・削除 API の絶対 URL を構築する */
  buildGroupDataUrl(
    reporterId: string,
    groupId: string,
    parentGroupId?: GroupParentContext,
    withChildren?: boolean,
  ): string {
    return this.buildGroupUrl(environment.groupApi.endpoints.data, reporterId, {
      fileSystemId: this.buildGroupFileSystemId(groupId, parentGroupId),
      withChildren,
    });
  }

  /** 全 Group 取得 API の絶対 URL を構築する */
  buildGroupListUrl(reporterId: string, parentGroupId?: GroupParentContext): string {
    return this.buildGroupUrl(environment.groupApi.endpoints.list, reporterId, {
      searchPath: this.buildGroupSearchPath(parentGroupId),
    });
  }

  /** Group targets 更新・削除 API の絶対 URL を構築する */
  buildGroupNodesUrl(reporterId: string, groupId: string, parentGroupId?: GroupParentContext): string {
    return this.buildGroupUrl(environment.groupApi.endpoints.nodes, reporterId, {
      fileSystemId: this.buildGroupFileSystemId(groupId, parentGroupId),
      keyName: environment.groupApi.keyName,
      jsonFileType: environment.groupApi.jsonFileType,
      baseObjectPath: environment.groupApi.baseObjectPath,
      secure: environment.groupApi.secure,
      waiting: environment.groupApi.waiting,
      bucketCode: environment.groupApi.bucketCode,
    });
  }

  /** Group 部分更新 API の絶対 URL を構築する */
  buildGroupPatchUrl(reporterId: string, groupId: string, parentGroupId?: GroupParentContext): string {
    return this.buildGroupUrl(environment.groupApi.endpoints.patch, reporterId, {
      fileSystemId: this.buildGroupFileSystemId(groupId, parentGroupId),
    });
  }

  /** 全 API で共通利用する既定パスパラメータ */
  private get defaultPathParams(): PathParams {
    return {
      reporterId: this.config.reporterId,
    };
  }

  /** Group JSON の fileSystemId を生成する */
  private buildGroupFileSystemId(groupId: string, parentGroupId?: GroupParentContext): string {
    const normalizedGroupId = String(groupId).trim();
    if (!normalizedGroupId) {
      throw new Error('Missing required groupId for Group API endpoint.');
    }

    const normalizedParentGroupId = this.normalizeOptionalValue(parentGroupId);
    return normalizedParentGroupId
      ? `${environment.groupApi.fileSystemRoot}/${normalizedParentGroupId}/${normalizedGroupId}`
      : `${environment.groupApi.fileSystemRoot}/${normalizedGroupId}`;
  }

  /** Group 一覧取得用の searchPath を生成する */
  private buildGroupSearchPath(parentGroupId?: GroupParentContext): string {
    const normalizedParentGroupId = this.normalizeOptionalValue(parentGroupId);
    return normalizedParentGroupId
      ? `${environment.groupApi.fileSystemRoot}/${normalizedParentGroupId}/*`
      : environment.groupApi.searchPath;
  }

  /** Group API 用の絶対 URL を構築する */
  private buildGroupUrl(template: string, reporterId: string, queryParams?: QueryParams): string {
    return this.buildUrl(
      environment.groupApi.baseUrl,
      template,
      {
        reporterId,
        indexAppCd: environment.app.indexAppCd,
      },
      {
        bucketCode: environment.groupApi.bucketCode,
        ...queryParams,
      },
    );
  }

  /** Base URL と相対パスを結合して絶対 URL を生成する */
  private buildUrl(
    baseUrl: string,
    template: string,
    pathParams?: PathParams,
    queryParams?: QueryParams,
  ): string {
    /** 末尾スラッシュを正規化した Base URL */
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    return `${normalizedBaseUrl}${this.buildRelativeUrl(template, pathParams, queryParams)}`;
  }

  /** パスとクエリを組み立てて相対 URL を生成する */
  private buildRelativeUrl(
    template: string,
    pathParams?: PathParams,
    queryParams?: QueryParams,
  ): string {
    /** 置換後の URL パス */
    const path = this.resolvePathTemplate(template, pathParams);

    /** URLSearchParams 形式に変換したクエリ文字列 */
    const search = this.buildQueryString(queryParams);
    return search ? `${path}?${search}` : path;
  }

  /** {reporterId} 形式のプレースホルダーを実値へ置換する */
  private resolvePathTemplate(template: string, pathParams?: PathParams): string {
    return template.replace(/\{(\w+)\}/g, (_match, key: string) => {
      /** 対応する置換値 */
      const rawValue = pathParams?.[key];

      if (rawValue === undefined || rawValue === null || String(rawValue).trim() === '') {
        throw new Error(`Missing required path parameter: ${key}`);
      }

      return encodeURIComponent(String(rawValue));
    });
  }

  /** クエリパラメータ辞書を URLSearchParams 形式へ変換する */
  private buildQueryString(queryParams?: QueryParams): string {
    if (!queryParams) {
      return '';
    }

    /** null / undefined / 空文字を除外して格納する */
    const searchParams = new URLSearchParams();

    for (const [key, rawValue] of Object.entries(queryParams)) {
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        continue;
      }

      searchParams.set(key, String(rawValue));
    }

    return searchParams.toString();
  }

  /** 任意文字列を trim し、空なら null を返す */
  private normalizeOptionalValue(value: Primitive | null | undefined): string | null {
    const normalizedValue = String(value ?? '').trim();
    return normalizedValue ? normalizedValue : null;
  }
}
